import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PaymentModalOutput } from '@payments/services/payments-modal.service';
import { SortDirection, SortFilter } from '@usf/ngrx-payment';
import { SortFields } from '@usf/payment-types';
import { CustomerSelectionPaymentsTouchComponent } from '../header/customer-selection/customer-selection-payments-touch/customer-selection-payments-touch.component';
import { UsfFilterHostDirective } from './filter-host-directive/filter-host.directive';
import {
  ModalFilter,
  ModalFilterCalendarData,
  ModalFilterCustomerData,
  ModalFilterDataType,
  ModalFilterTextData,
  ModalSort,
  isFilterCalendarData,
  isFilterCustomerData,
  isFilterListData,
  isFilterTextEntryData,
  isSortOptionsData,
} from './model/modal-filter';
import { UsfFilteringOptionComponent } from './usf-filtering-options/usf-filtering-option/usf-filtering-option-component';

@Component({
  template: '',
})
export class UsfFilteringModalComponent implements OnInit {
  @ViewChild(UsfFilterHostDirective, { static: true })
  adHost!: UsfFilterHostDirective;

  @Input() isTouch: boolean;
  @Input() filters: ModalFilter[];
  @Input() selectedFilterIndex: number;
  @Input() title: string;
  @Input() sortSelection: SortFilter[];
  @Input() sortOptions?: ModalSort[];
  @Input() sortComponent;
  @Input() cancelButton?: boolean;
  @Input() disableSort?: boolean;
  @Input() isBusinessAnalytics?: boolean;

  @Output() clearFiltersEvent: EventEmitter<ModalFilter[]>;
  @Output() setSortEvent: EventEmitter<SortFilter[]>;

  totalFilters = 0;
  disableApplyFiltersButton = false;

  constructor(private modalController: ModalController) {}

  ngOnInit(): void {
    this.selectFilter(this.selectedFilterIndex);
    this.updateCountsAndSubtitles();
  }

  clearFilters() {
    this.totalFilters = 0;
    this.clearFiltersEvent.emit(this.filters);
    this.showSelectedFilter();
    this.updateCountsAndSubtitles();
  }

  selectFilter(index: number = this.selectedFilterIndex) {
    this.selectedFilterIndex = index;
    this.showSelectedFilter();
  }

  updateCountsAndSubtitles() {
    if (this.isBusinessAnalytics) {
      this.disableApplyFiltersButton = true;
    }
    let count = 0;
    let dateSelected = false;
    let customerSelected = 0;
    this.filters?.forEach(filter => {
      if (
        isFilterListData(filter.data) &&
        !!filter.data.newSelectedData?.length
      ) {
        count += filter.data.newSelectedData.length;
      } else if (
        isFilterCalendarData(filter.data) &&
        !!(filter.data as ModalFilterCalendarData).fromDate &&
        !!(filter.data as ModalFilterCalendarData).toDate
      ) {
        dateSelected = true;
        count++;
      } else if (isFilterCustomerData(filter.data)) {
        customerSelected = filter.data.selectedCustomers.length;
        count += filter.data.selectedCustomers.length;
      } else if (
        isFilterTextEntryData(filter.data) &&
        (filter.data as ModalFilterTextData).searchText.length > 0
      ) {
        count += 1;
      }

      filter.data.subTitle$.next(this.subTitleForFilter(filter.data));
    });
    this.totalFilters = count;

    if (dateSelected && customerSelected) {
      this.disableApplyFiltersButton = false;
    }
  }

  async dismiss() {
    await this.modalController.dismiss();
  }

  async applyFilters() {
    if (this.sortComponent.data.sortSelection !== this.sortSelection) {
      this.sortSelection = this.sortComponent.data.sortSelection;
      if (
        this.sortComponent.data.sortOptions.some(
          sort => sort.fieldName === SortFields.transaction,
        ) &&
        this.sortSelection[0].fieldName !== SortFields.transaction
      ) {
        this.sortSelection.push({
          fieldName: SortFields.transaction,
          sortValue: SortDirection.DESC,
        });
      }
    }

    await this.modalController.dismiss({
      filters: this.filters,
      sort: this.sortSelection,
    } as PaymentModalOutput);
  }

  showSortByComponent() {
    const modalFilter = this.sortComponent;
    const viewContainerRef = this.adHost.viewContainerRef;
    viewContainerRef.clear();

    if (modalFilter.component) {
      const componentRef = viewContainerRef.createComponent(
        this.sortComponent.component,
      );
      if (componentRef.instance instanceof UsfFilteringOptionComponent) {
        componentRef.instance.data = this.sortComponent.data;
        componentRef.instance.isTouch = false;
        this.selectedFilterIndex = -1;
        const subscription = componentRef.instance.optionSelected.subscribe(
          a => {
            const data = modalFilter.data;
            if (isSortOptionsData(data)) {
              this.sortComponent.data.sortSelection = data.sortSelection;
              if (
                this.sortComponent.data.sortOptions.some(
                  sort => sort.fieldName === SortFields.transaction,
                ) &&
                this.sortComponent.data.sortSelection[0].fieldName !==
                  SortFields.transaction &&
                this.sortOptions
              ) {
                this.sortSelection.push({
                  fieldName: SortFields.transaction,
                  sortValue: SortDirection.DESC,
                });
              }
            }
          },
        );
        componentRef.onDestroy(() => {
          subscription.unsubscribe();
        });
      }
    }
  }

  private showSelectedFilter() {
    if (
      !!this.filters &&
      !!this.filters[this.selectedFilterIndex] &&
      !!this.adHost
    ) {
      const modalFilter = this.filters[this.selectedFilterIndex];

      const viewContainerRef = this.adHost.viewContainerRef;
      viewContainerRef.clear();
      if (modalFilter.component) {
        const componentRef = viewContainerRef.createComponent(
          modalFilter.component,
        );

        if (componentRef.instance instanceof UsfFilteringOptionComponent) {
          componentRef.instance.data =
            this.filters[this.selectedFilterIndex].data;
          const subscription = componentRef.instance.optionSelected.subscribe(
            a => {
              const data = modalFilter.data;
              if (isFilterListData(data)) {
                data.newSelectedData = a;
              } else if (isFilterTextEntryData(data)) {
                data.searchText = a;
              }
              this.updateCountsAndSubtitles();
            },
          );
          componentRef.onDestroy(() => {
            subscription.unsubscribe();
          });
        } else if (
          componentRef.instance instanceof
          CustomerSelectionPaymentsTouchComponent
        ) {
          const customerData = modalFilter.data as ModalFilterCustomerData;
          componentRef.instance.show = customerData.show;
          componentRef.instance.vm$ = customerData.vm$;
          componentRef.instance.searchText$ = customerData.searchText$;
          componentRef.instance.isPresentedInFilterModal =
            customerData.isPresentedInFilterModal;

          componentRef.instance.selectedCustomers =
            customerData.selectedCustomers;
          const subscription =
            componentRef.instance.customersToFilterSelected.subscribe(a => {
              customerData.selectedCustomers = a;
              this.updateCountsAndSubtitles();
            });
          componentRef.onDestroy(() => {
            componentRef.instance.searchText$.next('');
            subscription.unsubscribe();
          });
        }
      }
    }
  }

  protected subTitleForFilter(data: ModalFilterDataType) {
    if (isFilterListData(data)) {
      return 'Selected: ' + (data.newSelectedData.length ?? 0);
    } else if (isFilterCalendarData(data)) {
      return this.formattedDateLabel(data.fromDate, data.toDate);
    } else if (isFilterTextEntryData(data)) {
      return (
        'Selected: ' +
        ((data as ModalFilterTextData).searchText.length > 0 ? 1 : 0)
      );
    } else if (isFilterCustomerData(data)) {
      return (
        'Selected: ' +
        ((data as ModalFilterCustomerData).selectedCustomers.length ?? 0)
      );
    }
    return '';
  }

  private formattedDateFromNumber(date: number) {
    if (!!date) {
      const dateString = date.toString();
      return (
        dateString.substring(4, 6) +
        '/' +
        dateString.substring(6, 8) +
        '/' +
        dateString.substring(0, 4)
      );
    }
    return 'MM/DD/YYYY';
  }

  protected formattedDateLabel(fromDate: number, toDate: number) {
    return (
      'From ' +
      this.formattedDateFromNumber(fromDate) +
      ' to ' +
      this.formattedDateFromNumber(toDate)
    );
  }
}
